import React, { useEffect, useState } from 'react';
import { Skeleton, Tree } from 'antd';
import type { TreeDataNode, TreeProps } from 'antd';
import usePagination from '../hooks/usePagination';
import { IAssetType } from '../models/AssetsTypes';
import { IAsset } from '../models/Assets';
import { IProject } from '../models/Project';
import { title } from 'process';

type Props = {
    hookProjects: ReturnType<typeof usePagination<IProject>>
    hookAssetsTypes: ReturnType<typeof usePagination<IAssetType>>
    hookAssets: ReturnType<typeof usePagination<IAsset>>,
    checkedKeys: TreeProps['checkedKeys'],
    setCheckedKeys: (checkedKeys: TreeProps['checkedKeys']) => void;
}

const TreeComp: React.FC<Props> = ({ hookAssetsTypes, hookAssets, hookProjects, checkedKeys, setCheckedKeys }) => {
    const [treeData, setTreeData] = useState<TreeDataNode[]>([]);
    const [defaultExpandedKeys, setDefaultExpandedKeys] = useState<string[]>([]);
    const [defaultSelectedKeys, setDefaultSelectedKeys] = useState<string[]>([]);
    const [defaultCheckedKeys, setDefaultCheckedKeys] = useState<string[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<TreeProps['expandedKeys']>([]);

    const [treeDataAll, setTreeDataAll] = useState<TreeDataNode[]>([]);
    const [checkedKeysAll, setCheckedKeysAll] = useState<TreeProps['checkedKeys']>([]);
    const [defaultExpandedKeysAll, setDefaultExpandedKeysAll] = useState<string[]>([]);
    const [defaultSelectedKeysAll, setDefaultSelectedKeysAll] = useState<string[]>([]);
    const [defaultCheckedKeysAll, setDefaultCheckedKeysAll] = useState<string[]>([]);
    const [expandedKeysAll, setExpandedKeysAll] = useState<TreeProps['expandedKeys']>([]);

    const [loading, setLoading] = useState<boolean>(true);


    useEffect(() => {
        if (!hookAssetsTypes.loading && !hookAssets.loading && !hookProjects.loading) {
            const tree: TreeDataNode[] = hookProjects.data.map(currentProject => {
                return {
                    title: `${currentProject.name}`,
                    key: `${currentProject.id}`,
                    children: hookAssetsTypes.data.map(assetType => {
                        const childrenItems = hookAssets.data
                            .filter(asset => asset.type_id === assetType.id && asset.project_id === currentProject.id)
                            .map(asset => ({ title: asset.description, key: `${currentProject.id}-${assetType.id}-${asset.id}` }));
                        return {
                            title: `${assetType.name} (${getCountType(currentProject.id, assetType.id)})`,
                            key: `${currentProject.id}-${assetType.id}`,
                            children: childrenItems
                        }
                    })
                }
            });
            const treeAll: TreeDataNode[] = [{
                title: 'Todos los tramos',
                key: 'all-assets',
                children: hookAssetsTypes.data.map((currentAssetType: IAssetType) => {
                    return {
                        title: `${currentAssetType.name} (${getCountTypeAll(currentAssetType.id)})`,
                        key: `${currentAssetType.id}`,
                        children: hookAssets.data
                            .filter((asset: IAsset) => asset.type_id === currentAssetType.id)
                            .map((asset: IAsset) => ({
                                title: asset.description,
                                key: `${currentAssetType.id}-${asset.id}`,
                            }))
                    }
                })
            }]
            setTreeDataAll(treeAll);
            setTreeData(tree);
            setLoading(false);
        } else {
            setLoading(true);
        }
    }, [hookAssetsTypes.loading, hookAssets.loading, hookProjects.loading])

    useEffect(() => {
        if (treeData.length > 0) {
            setExpandedKeys(['all-assets']);
        }
    }, [treeData]);

    useEffect(() => {
        const keysArray = Array.isArray(checkedKeys) ? checkedKeys : [];
        const checkedAssets = keysArray
            .filter(key => key.toString().split('-').length === 3)
            .map(key => {
                const checkedKey = key.toString().replace(/^\d+-/, "");
                return checkedKey;
            })
        setCheckedKeysAll(checkedAssets)
    }, [checkedKeys]);

    const getCountType = (project_id: number, asset_type_id: number) => {
        const assetsCount = hookAssets.data.filter(asset => asset.type_id === asset_type_id && asset.project_id === project_id)
        return assetsCount.length;
    }
    const getCountTypeAll = (asset_type_id: number) => {
        const assetsCount = hookAssets.data.filter(asset => asset.type_id === asset_type_id)
        return assetsCount.length;
    }
    const onSelect: TreeProps['onSelect'] = (selectedKeys, info) => {
        console.log('selected', selectedKeys, info);
    };
    const onSelectAll: TreeProps['onSelect'] = (selectedKeys, info) => {
        console.log('selected', selectedKeys, info);
    };
    const onCheck: TreeProps['onCheck'] = (checkedKeys, info) => {
        console.log('onCheck', checkedKeys, info);
        setCheckedKeys(checkedKeys);
    };
    const onCheckAll: TreeProps['onCheck'] = (checkedKeys, info) => {
        const keysArray = Array.isArray(checkedKeys) ? checkedKeys : [];
        const checkedAssets = keysArray
            .filter(key => key.toString().includes('-'))
            .map(key => {
                const assetId = key.toString().split('-')[1];
                const currentAsset = hookAssets.data.find((asset: IAsset) => asset.id === Number(assetId));
                return `${currentAsset?.project_id}-${key}`
            })
        setCheckedKeys(checkedAssets)
    };
    const onExpand: TreeProps['onExpand'] = (expandedKeys, info) => {
        console.log('onExpand', expandedKeys, info);
        setExpandedKeys(expandedKeys);
    };
    const onExpandAll: TreeProps['onExpand'] = (expandedKeys, info) => {
        console.log('onExpand', expandedKeys, info);
        setExpandedKeysAll(expandedKeys);
    };

    return (<>
        {
            loading ? <>
                <Skeleton active round />
                <Skeleton active round />
                <Skeleton active round />
                <Skeleton active round />
                <Skeleton active round />
                <Skeleton active round />
            </> : <>
                {/* Todos los tramos */}
                <Tree
                    checkable
                    selectable
                    onExpand={onExpandAll}
                    checkedKeys={checkedKeysAll}
                    expandedKeys={expandedKeysAll}
                    defaultExpandedKeys={defaultExpandedKeysAll}
                    defaultSelectedKeys={defaultSelectedKeysAll}
                    defaultCheckedKeys={defaultCheckedKeysAll}
                    onSelect={onSelectAll}
                    onCheck={onCheckAll}
                    treeData={treeDataAll}
                />
                {/* Separado por tramos */}
                <Tree
                    checkable
                    selectable
                    onExpand={onExpand}
                    checkedKeys={checkedKeys}
                    expandedKeys={expandedKeys}
                    defaultExpandedKeys={defaultExpandedKeys}
                    defaultSelectedKeys={defaultSelectedKeys}
                    defaultCheckedKeys={defaultCheckedKeys}
                    onSelect={onSelect}
                    onCheck={onCheck}
                    treeData={treeData}
                />
            </>
        }
    </>);
};

export default TreeComp;